import { SELECTED_NETWORKS_KEY } from '@/constants/state'
import type { Vault } from '@/types/vault'
import { getCuratorDisplayName } from '@/utils/curator'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { APP_URL } from '@vaultsfyi/common/constants'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { getMetaTags } from './getMetaTags'
import { getVaultStructuredData } from './getVaultStructuredData'

interface Props {
  vault: Vault | undefined
  pathname: string
}

export function getOpportunityMeta({ vault, pathname }: Props) {
  const vaultName = vault?.name ?? 'Opportunity'
  const protocolDisplay = vault?.protocol ? getProtocolDisplayName(vault.protocol) : ''
  const networkRaw = vault?.network?.name
  const networkName = networkRaw ? getNetworkName(networkRaw) : ''
  const networkDisplay = networkName.charAt(0).toUpperCase() + networkName.slice(1)
  const onchainSymbol = vault?.lpToken?.symbol
  const curatorName = vault?.curator ? getCuratorDisplayName(vault.curator) : undefined
  const vaultAddress = vault?.address
  const assetSymbol = vault?.asset?.symbol

  const apy7d = vault?.apy?.['7day']?.total
  const apyCopy = apy7d !== undefined ? formatDecimalToPercentage(apy7d, { notation: 'standard' }) : ''

  const tvlUsd = vault?.tvl?.usd
  const tvlCopy = tvlUsd !== undefined ? formatUSD(tvlUsd) : ''

  const titleSegments = [`${vaultName}${onchainSymbol ? ` (${onchainSymbol})` : ''}`]
  if (protocolDisplay) titleSegments.push(`by ${protocolDisplay}`)
  if (networkDisplay) titleSegments.push(`on ${networkDisplay}`)
  const title = titleSegments.join(' ')

  const vaultIdentity = `${vaultName}${onchainSymbol ? ` (${onchainSymbol})` : ''}`
  const vaultType = protocolDisplay ? `${protocolDisplay} vault` : 'vault'
  const networkCopy = networkDisplay ? ` on ${networkDisplay}` : ''
  const descSegments = [
    curatorName
      ? `${vaultIdentity} is a ${vaultType} curated by ${curatorName}${networkCopy}.`
      : `${vaultIdentity} is a ${vaultType}${networkCopy}.`,
  ]
  if (vaultAddress) descSegments.push(`Vault contract: ${vaultAddress}.`)
  if (assetSymbol) descSegments.push(`Underlying asset: ${assetSymbol}.`)
  const description = descSegments.join(' ')

  const pageUrl = `${APP_URL}${pathname}`
  const structuredData: Array<Record<string, unknown>> = []

  if (vault) {
    const financialProductDesc = [
      apyCopy ? `${apyCopy} 7-day APY` : '',
      tvlCopy ? `${tvlCopy} TVL` : '',
      networkDisplay ? `on ${networkDisplay}` : '',
    ]
      .filter(Boolean)
      .join(', ')
      .replace(', on ', ' on ')

    structuredData.push(
      getVaultStructuredData({
        vault,
        pageUrl,
        description: financialProductDesc,
        interestRate: apy7d !== undefined ? Math.round(apy7d * 10000) / 100 : undefined,
      })
    )

    const breadcrumbs: Array<Record<string, unknown>> = [
      { '@type': 'ListItem', position: 1, name: 'vaults.fyi', item: APP_URL },
    ]
    if (networkDisplay && networkRaw) {
      breadcrumbs.push({
        '@type': 'ListItem',
        position: 2,
        name: networkDisplay,
        item: `${APP_URL}/?${SELECTED_NETWORKS_KEY}=${networkRaw}`,
      })
    }
    breadcrumbs.push({
      '@type': 'ListItem',
      position: breadcrumbs.length + 1,
      name: vaultName,
      item: pageUrl,
    })

    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs,
    })
  }

  return getMetaTags({ title, description, url: pathname, structuredData })
}
